import { Button } from "@/components/core/buttons/Button";
import { cn } from "@/lib/utils";
import { UniqueIdentifier } from "@dnd-kit/core";
import { Menu } from "components/core/Menu";
import { Text } from "components/core/Text";
import { IconButton } from "components/core/buttons/IconButton";
import { DndContainer } from "components/core/dnd/DndContainer";
import { useChromeStorage } from "hooks/useChromeStorage";
import Image from "next/image";
import { useEffect } from "react";
import { GOOGLE_APPS } from "utils/enum";

export function ChromeGoogleAppMenu() {
    const [googleAppOrder, setGoogleAppOrder] = useChromeStorage<Array<string>>(
        "googleAppOrder",
        [],
        "local"
    );

    useEffect(() => {
        (async () => {
            const storage = await chrome.storage.local.get("googleAppOrder");

            if (storage.googleAppOrder) {
                setGoogleAppOrder(storage.googleAppOrder);
            } else {
                const defaultOrder = Object.keys(GOOGLE_APPS);
                setGoogleAppOrder(defaultOrder);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDragEnd = (changedAppTitleOrder: UniqueIdentifier[]) => {
        chrome.storage.local.set({ googleAppOrder: changedAppTitleOrder });
    };

    return (
        <Menu
            anchorEl={({ open }) => (
                <IconButton
                    className={cn(
                        "[&_svg]:text-grey-600",
                        "hover:!bg-action-hover",
                        open && "bg-action-selected"
                    )}
                    name="GoogleAppButton"
                    size="medium"
                    variant="background"
                    color="grey"
                />
            )}
            placement="bottom-start"
            offset={4}
        >
            <ul
                className="grid max-h-[400px] w-[262px] grid-cols-3
                    overflow-x-hidden overflow-y-scroll overscroll-contain px-2"
            >
                <DndContainer
                    onDragEnd={handleDragEnd}
                    renderItems={googleAppOrder
                        .map((name) => {
                            const googleApp = GOOGLE_APPS[name];
                            return (
                                googleApp && (
                                    <ChromeGoogleAppMenuItem
                                        key={name as UniqueIdentifier}
                                        name={name}
                                        url={googleApp.url}
                                        imgSrc={googleApp.imgSrc}
                                    />
                                )
                            );
                        })
                        .filter(
                            (item): item is React.ReactElement =>
                                item !== undefined
                        )}
                />
            </ul>
        </Menu>
    );
}

interface ChromeGoogleAppMenuItemProps {
    key: UniqueIdentifier;
    name: string;
    url: string;
    imgSrc: string;
}

const ChromeGoogleAppMenuItem: React.FC<ChromeGoogleAppMenuItemProps> = ({
    key,
    name,
    url,
    imgSrc
}) => {
    return (
        <Button
            key={key}
            className="h-[88px] w-[82px] rounded-xl px-0 py-2 pb-3
                hover:bg-action-hover data-[interaction=hover]:bg-action-hover"
            variant="background"
            color="secondary"
            onClick={() => {
                window.location.href = `${url}`;
            }}
        >
            <div className="flex w-[82px] flex-col items-center justify-center">
                <Image
                    alt={name}
                    src={`/img/google/${imgSrc}`}
                    width={50}
                    height={50}
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = "/img/google.png"; // Fallback image
                    }}
                />
                <Text variant="body4">{name}</Text>
            </div>
        </Button>
    );
};
